import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Wand2 } from 'lucide-react'
import { useGenerateMockups, useMockupTargets } from '../hooks/useContent'

/**
 * Kick off mockup generation for a product. Creates a draft (status 'review') the
 * curator then refines in the Mockup editor. Generation is slow (Gemini image gen +
 * Shopify Files upload per slide) — the dialog stays open until the draft is created.
 */
export function MockupGenerateDialog() {
  const navigate = useNavigate()
  const gen = useGenerateMockups()
  const { data: targets } = useMockupTargets()
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState('marketplace-pdp')
  const [handle, setHandle] = useState('')
  const [cdnId, setCdnId] = useState('')
  const [title, setTitle] = useState('')
  const [artistHandle, setArtistHandle] = useState('')
  const [templateRoom, setTemplateRoom] = useState('slow-linen')
  const [rooms, setRooms] = useState('quiet-earth, golden-hour')

  const activeTarget = (targets || []).find((t) => t.id === target)
  const isMarketplace = !activeTarget || activeTarget.surface === 'marketplace'
  // An artist target needs an artistHandle if the registry entry didn't bake one in.
  const needsArtist = !!activeTarget && activeTarget.style?.kind === 'artist' && !activeTarget.style?.artistHandle

  const submit = async () => {
    if (!cdnId.trim()) return
    if (isMarketplace && !handle.trim()) return
    const item = await gen.mutateAsync({
      target,
      handle: handle.trim() || undefined,
      artwork: { cdnId: cdnId.trim(), title: title.trim() || undefined, handle: handle.trim() || undefined },
      // Override the target's style only when supplying an artist for a generic artist target.
      style: needsArtist && artistHandle.trim() ? { kind: 'artist', artistHandle: artistHandle.trim() } : undefined,
      templateRoom: templateRoom.trim() || undefined,
      rooms: rooms.split(',').map((r) => r.trim()).filter(Boolean),
    })
    setOpen(false)
    navigate(`/app/content/${item.id}`)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Wand2 className="h-4 w-4 mr-2" />
          Generate mockups
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate product mockups</DialogTitle>
          <DialogDescription>
            Creates a faithful template-composite primary plus room scenes, uploads them to Shopify Files,
            and drops a draft into the queue for review.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Render target (surface — sets the style + display aspect)</Label>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
            >
              {(targets || [{ id: 'marketplace-pdp', label: 'Marketplace PDP', surface: 'marketplace', style: { kind: 'brand' }, display: {} }]).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}{t.display?.aspect ? ` · ${t.display.aspect}` : ''} · {t.style?.kind}
                </option>
              ))}
            </select>
          </div>
          {needsArtist && (
            <div className="space-y-1">
              <Label className="text-xs">Artist handle (collection handle — for artist styling)</Label>
              <Input value={artistHandle} onChange={(e) => setArtistHandle(e.target.value)} placeholder="kaethe-butcher" />
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">{isMarketplace ? 'Product handle (the ACTIVE one — beware -old/-1 dupes)' : 'Product handle (optional for non-marketplace targets)'}</Label>
            <Input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="time-to-leave" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Artwork CDN id</Label>
              <Input value={cdnId} onChange={(e) => setCdnId(e.target.value)} placeholder="28187-time-to-leave" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Title (optional)</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Time To Leave" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Template room (primary)</Label>
              <Input value={templateRoom} onChange={(e) => setTemplateRoom(e.target.value)} placeholder="slow-linen" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Scene rooms (comma-separated)</Label>
              <Input value={rooms} onChange={(e) => setRooms(e.target.value)} placeholder="quiet-earth, golden-hour" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            CDN id is the full <code>&lt;num&gt;-&lt;slug&gt;</code> (from picasso.arthaus.cloud/cache/artworks). You can
            regenerate, add rooms, reorder, and pick the primary after the draft is created.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={gen.isPending}>Cancel</Button>
          <Button onClick={submit} disabled={gen.isPending || !handle.trim() || !cdnId.trim()}>
            {gen.isPending ? 'Generating… (~30–60s)' : 'Generate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
